import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { LogOut, Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import Breadcrumbs from './Breadcrumbs';
import { Button } from '@/components/ui/button';

interface AdminHeaderProps {
  onMobileMenuToggle?: () => void;
  mobileMenuOpen?: boolean;
}

/**
 * Sticky header with breadcrumbs, user avatar, and logout button
 */
export default function AdminHeader({ onMobileMenuToggle, mobileMenuOpen }: AdminHeaderProps) {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/admin/login', { replace: true });
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return '?';
    if (user.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'A';
  };

  return (
    <header className="sticky top-0 z-30 h-16 bg-pine-pine-deep border-b border-pine-pine/30 font-body">
      <div className="h-full flex items-center justify-between px-4 lg:px-6">
        {/* Left: Mobile Menu + Breadcrumbs */}
        <div className="flex items-center gap-4">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onMobileMenuToggle}
            className="lg:hidden text-white/70 hover:text-white"
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </Button>

          {/* Breadcrumbs (hidden on small screens) */}
          <div className="hidden sm:block">
            <Breadcrumbs />
          </div>
        </div>

        {/* Right: User Info and Logout */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* User Email */}
          <div className="hidden md:block">
            <p className="text-body-sm text-white/60">
              {user?.email || 'Admin'}
            </p>
          </div>

          {/* User Avatar */}
          <div className="relative group">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-pine-gold flex items-center justify-center text-[#2A1F08] font-semibold shadow-lg text-sm sm:text-base">
              {getUserInitials()}
            </div>
            {/* Tooltip */}
            <div className="absolute right-0 top-full mt-2 bg-pine-ink text-white/80 text-caption px-3 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              {user?.email || 'Admin User'}
            </div>
          </div>

          {/* Logout Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-white/60 hover:text-white"
          >
            <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline ml-2">Logout</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
